import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Uses Emergent Auth service - redirect URL is derived dynamically from window.location.origin
const getGoogleAuthUrl = () => {
    const redirectUrl = window.location.origin;
    return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const hasProcessedCallback = useRef(false);
    const isTestUserLogin = useRef(false);

    // Single unified useEffect to handle all auth scenarios in proper order
    useEffect(() => {
        const initializeAuth = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const hash = window.location.hash;
            
            // Priority 1: OAuth callback with session_id in hash
            if (hash && hash.includes('session_id=')) {
                const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
                if (sessionId && !hasProcessedCallback.current) {
                    hasProcessedCallback.current = true;
                    await handleAuthCallback(sessionId);
                    return;
                }
            }
            
            // Priority 2: OAuth callback with session_id in query params
            const sessionIdFromQuery = urlParams.get('session_id');
            if (sessionIdFromQuery && !hasProcessedCallback.current) {
                hasProcessedCallback.current = true;
                await handleAuthCallback(sessionIdFromQuery);
                return;
            }
            
            // Priority 3: Test user login via ?testuser=pro
            const testUser = urlParams.get('testuser');
            if (testUser === 'pro') {
                isTestUserLogin.current = true;
                await loginAsTestUser();
                return;
            }
            
            // Priority 4: Check for existing session (normal auth check)
            await checkAuth();
        };
        
        initializeAuth();
    }, []);

    // Login as test user (for demo purposes)
    const loginAsTestUser = async () => {
        try {
            console.log('[AuthContext] Logging in as test user...');
            const response = await axios.post(`${API}/auth/test-login`, {}, { withCredentials: true });
            if (response.data) {
                console.log('[AuthContext] Test user login successful:', response.data.subscription_tier);
                setUser(response.data);
                // Clean only testuser param, keep tool and other params
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('testuser');
                window.history.replaceState({}, '', newUrl.toString());
            }
        } catch (error) {
            console.error('[AuthContext] Test login failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const checkAuth = async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, {
                withCredentials: true
            });
            setUser(response.data);
        } catch (error) {
            // Not authenticated
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    const handleAuthCallback = async (sessionId) => {
        try {
            const response = await axios.post(`${API}/auth/session`, 
                { session_id: sessionId },
                { withCredentials: true }
            );
            setUser(response.data);
            
            // Clean up URL - remove both hash and query params
            window.history.replaceState({}, '', window.location.pathname);
        } catch (error) {
            console.error('Auth callback failed:', error);
            // Show user-friendly message
            alert('Sign in failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const login = useCallback(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        window.location.href = getGoogleAuthUrl();
    }, []);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(null);
        }
    }, []);

    // Email/Password Login
    const loginWithEmail = useCallback(async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, 
            { email, password },
            { withCredentials: true }
        );
        setUser(response.data);
        return response.data;
    }, []);

    // Email/Password Registration
    const registerWithEmail = useCallback(async (name, email, password) => {
        const response = await axios.post(`${API}/auth/register`, 
            { name, email, password },
            { withCredentials: true }
        );
        setUser(response.data);
        return response.data;
    }, []);

    const refreshUser = useCallback(async () => {
        await checkAuth();
    }, []);

    const value = {
        user,
        loading,
        login,
        loginWithEmail,
        registerWithEmail,
        logout,
        refreshUser,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
